import React, { useState, useEffect } from 'react';
import NavigationArrowIcon from '../icons/NavigationArrowIcon';
import './UserGuideOverlay.css';

interface UserGuideOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserGuideOverlay: React.FC<UserGuideOverlayProps> = ({ isOpen, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleDownloadPDF = () => {
    const link = document.createElement('a');
    link.href = '/user-guide/Board Navigator - Orientation Guide.pdf';
    link.download = 'Board Navigator - Orientation Guide.pdf';
    link.click();
  };

  // Reset to first slide whenever overlay opens
  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
    }
  }, [isOpen]);

  // List of slide images
  const slides = [
    '/user-guide-slides/Pages-0.png',
    '/user-guide-slides/Pages-1.png',
    '/user-guide-slides/Pages-2.png',
    '/user-guide-slides/Pages-3.png',
    '/user-guide-slides/Pages-4.png',
    '/user-guide-slides/Pages-5.png'
  ];

  const totalSlides = slides.length;

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentSlide, onClose]);

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleNext = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="user-guide-overlay">
      <div className="user-guide-backdrop" onClick={onClose} />

      <div className="user-guide-content">
        {/* Image container */}
        <div className="user-guide-image-container">
          {/* PDF download button - positioned relative to image */}
          <button className="user-guide-pdf" onClick={handleDownloadPDF} aria-label="Download PDF">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 12 12" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M0.780159 0.281178C0.959541 0.101143 1.20284 0 1.45652 0H7.19565C7.32249 0 7.44414 0.0505713 7.53383 0.140589L10.8817 3.50059C10.9714 3.59061 11.0217 3.7127 11.0217 3.84V6.24C11.0217 6.5051 10.8076 6.72 10.5435 6.72C10.2793 6.72 10.0652 6.5051 10.0652 6.24V4.32001H7.19565C6.93152 4.32001 6.71739 4.10511 6.71739 3.84001V0.96L1.45652 0.96L1.45652 6.24C1.45652 6.5051 1.2424 6.72 0.978261 6.72C0.714125 6.72 0.5 6.5051 0.5 6.24V0.96C0.5 0.705392 0.600776 0.461212 0.780159 0.281178ZM7.67391 1.63882L9.38886 3.36001H7.67391V1.63882Z" fill="#003DA6"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M0.978261 8.16C0.714125 8.16 0.5 8.37491 0.5 8.64V11.52C0.5 11.7851 0.714125 12 0.978261 12C1.2424 12 1.45652 11.7851 1.45652 11.52V11.04H1.93478C2.31531 11.04 2.68025 10.8883 2.94933 10.6182C3.2184 10.3482 3.36957 9.98192 3.36957 9.6C3.36957 9.21809 3.2184 8.85182 2.94933 8.58177C2.68025 8.31172 2.31531 8.16 1.93478 8.16H0.978261ZM1.93478 10.08H1.45652V9.12H1.93478C2.06163 9.12 2.18327 9.17058 2.27296 9.26059C2.36266 9.35061 2.41304 9.4727 2.41304 9.6C2.41304 9.72731 2.36266 9.8494 2.27296 9.93941C2.18327 10.0294 2.06163 10.08 1.93478 10.08Z" fill="#003DA6"/>
              <path d="M9.34783 8.16C9.08369 8.16 8.86957 8.37491 8.86957 8.64V11.52C8.86957 11.7851 9.08369 12 9.34783 12C9.61197 12 9.82609 11.7851 9.82609 11.52V10.8H10.7826C11.0467 10.8 11.2609 10.5851 11.2609 10.32C11.2609 10.0549 11.0467 9.84 10.7826 9.84H9.82609V9.12H11.0217C11.2859 9.12 11.5 8.9051 11.5 8.64C11.5 8.37491 11.2859 8.16 11.0217 8.16H9.34783Z" fill="#003DA6"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M4.92391 8.16C4.65977 8.16 4.44565 8.37491 4.44565 8.64V11.52C4.44565 11.7851 4.65977 12 4.92391 12H5.76087C6.26824 12 6.75483 11.7977 7.11359 11.4376C7.47236 11.0776 7.67391 10.5892 7.67391 10.08C7.67391 9.57079 7.47236 9.08243 7.11359 8.72236C6.75483 8.36229 6.26824 8.16 5.76087 8.16H4.92391ZM5.76087 11.04H5.40217V9.12H5.76087C6.01455 9.12 6.25785 9.22115 6.43723 9.40118C6.61661 9.58122 6.71739 9.8254 6.71739 10.08C6.71739 10.3346 6.61661 10.5788 6.43723 10.7588C6.25785 10.9389 6.01455 11.04 5.76087 11.04Z" fill="#003DA6"/>
            </svg>
          </button>

          {/* Close button - positioned relative to image */}
          <button className="user-guide-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12" stroke="#222325" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4 4L12 12" stroke="#222325" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <img
            src={slides[currentSlide]}
            alt={`User guide slide ${currentSlide + 1} of ${totalSlides}`}
            className="user-guide-image"
          />

          {/* Previous arrow - positioned relative to image */}
          <div className="user-guide-nav user-guide-nav-prev" style={{ left: '-88px' }}>
            <NavigationArrowIcon
              direction="left"
              isActive={currentSlide > 0}
              onClick={handlePrevious}
              disabled={currentSlide === 0}
            />
          </div>

          {/* Next arrow - positioned relative to image */}
          <div className="user-guide-nav user-guide-nav-next" style={{ right: '-88px' }}>
            <NavigationArrowIcon
              direction="right"
              isActive={currentSlide < totalSlides - 1}
              onClick={handleNext}
              disabled={currentSlide === totalSlides - 1}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserGuideOverlay;